import {
	BadgeState,
	C_ObjectType,
	C_PictureTypeCustom,
	GraphicActionType,
	GraphicPageMode,
	ImageType,
	ObjectAppearanceProperties,
	ObjectFontProperties,
	ObjectPositionProperties,
} from '../../model/GraphicModel';
import { BadgeAction } from './actions';
import { BadgeActionTypes } from './actionsTypes';

const defaultAppearanceInfo: ObjectAppearanceProperties = {
	Id: 1,
	Visible: true,
	LineWidth: 1,
	LineColor: '#000000',
	Transparency: 0,
	BackColor: '#FFFFFF',
	BackStyle: 1,
	FillColor: '#FFFFFF',
	FillStyle: 1,
	LineStyle: 1,
};

const defaultPositionInfo: ObjectPositionProperties = {
	Id: 1,
	Bottom: 0,
	Left: 0,
	Right: 0,
	Rotation: 0,
	Top: 0,
};

const defaultFontInfo: ObjectFontProperties = {
	Id: 1,
	FontFamily: 'Tahoma',
	FontSize: 12,
	FontStyle: 0,
	TextColorHex: '#0F0F0F',
	AutoSize: false,
	DisplayText: 'sample text',
	FontContainerStyle: 1,
};

const INIT_STATE: BadgeState = {
	badgeInfo: {
		CreatedByUserId: 0,
		CreatedDate: new Date(),
		Id: 0,
		LastUpdated: new Date(),
		LastUpdatedByUserId: 0,
		Name: '',
		PreviewImage: '',
		Status: 0,
		FileExtension: '.jpg',
	},
	graphicInfo: {
		Id: -1,
		Name: '',
	},
	graphicActionType: GraphicActionType.Directory,
	currentRedlineConfigValues: null,
	drawingMenuOptsDisabled: false,
	dropDataNodes: [],
	error: '',
	errorMessages: [],
	fontFamilies: null,
	hasChanged: false,
	spaceObjects: {
		Id: 1,
		DBName: '',
		FileName: '',
		BadgeMagstripes: [
			// TODO: Delete this when data can be retrieved from DB
			{
				Id: 1,
				MagstripesData: '1',
				Name: 'Test MagStripe',
				ObjType: C_PictureTypeCustom.MDOT_DBMAGSTRIPE,
				PositionInfo: defaultPositionInfo,
				AppearanceInfo: defaultAppearanceInfo,
				MagstripeDisplayText: 'sample text',
				MagstripeFont: 'Tahoma',
				MagstripeFontColor: '#0f0f0f',
			},
		],
		BarCodes: [
			// TODO: Delete this when data can be retrieved from DB
			{
				Id: 1,
				AppendLuhnCheckDigit: true,
				Comment: 'Test comment',
				CommentOnTop: true,
				Data: 'Test data',
				Height: 1,
				IncludeText: true,
				Name: 'Test Barcode',
				NarrowBarWidth: 1,
				NarrowWideRatio: 1,
				ObjType: C_PictureTypeCustom.MDOT_DBBARCODE,
				PositionInfo: defaultPositionInfo,
				ReductionGain: 1,
				ShowBearerBars: true,
				ShowCheckCharacter: true,
				ShowQuietZones: true,
				Supplemental: 'Test supplemental',
				Symbology: 1,
				TextOnTop: true,
				BarcodeBackgroundStyle: false,
				BarcodeFont: 'Tahoma',
				BarcodeFontColor: '#0f0f0f',
				BarcodeVisible: true,
			},
		],
		Images: [
			// TODO: Delete this when data can be retrieved from DB
			{
				Id: 1,
				ImageType: ImageType.Photo,
				Name: 'Test Image',
				FileExtension: '.jpg',
				ImageDataBase64: '',
				ObjType: C_PictureTypeCustom.MDOT_IMAGE,
				PositionInfo: defaultPositionInfo,
				AppearanceInfo: defaultAppearanceInfo,
			},
		],
		ShapeObjects: [
			// TODO: Delete this when data can be retrieved from DB
			{
				Id: 1,
				Name: 'Test ShapeObject',
				ObjType: C_PictureTypeCustom.MDOT_VSO,
				PositionInfo: {
					Id: 1,
					Bottom: 0,
					Left: 0,
					Right: 0,
					Rotation: 0,
					Top: 0,
				},
				ShapeType: 1,
				UDF: 2,
				AppearanceInfo: defaultAppearanceInfo,
			},
		],
		Texts: [
			// TODO: Delete this when data can be retrieved from DB
			{
				ObjType: C_ObjectType.OT_TEXT,
				DataField: 'Service Number',
				FontInfo: defaultFontInfo,
				Id: 1,
				Name: 'Test Text',
				PositionInfo: defaultPositionInfo,
				UDF: 2,
				AppearanceInfo: defaultAppearanceInfo,
			},
			{
				ObjType: C_ObjectType.OT_TEXT,
				DataField: '',
				FontInfo: defaultFontInfo,
				Id: 2,
				Name: 'Test Name 2',
				PositionInfo: defaultPositionInfo,
				UDF: 2,
				AppearanceInfo: defaultAppearanceInfo,
			},
		],
	},
	udfs: null,
	udfs256: null,
	viewMode: GraphicPageMode.Design,
};

const badgeReducer = (state: Readonly<BadgeState> = INIT_STATE, action: BadgeAction): BadgeState => {
	switch (action.type) {
		//TODO: Add missing elements
		case BadgeActionTypes.SET_SPACE_OBJECT_DATA:
			const {
				payload: { FileName: BadgeFileName, BadgeMagstripes, BarCodes, ShapeObjects, Texts, Images },
			} = action;
			return {
				...state,
				spaceObjects: {
					FileName: BadgeFileName,
					BadgeMagstripes,
					BarCodes,
					ShapeObjects,
					Texts,
					Images,
				},
			};
		case BadgeActionTypes.SET_ACTION_TYPE:
			return {
				...state,
				graphicActionType: action.payload,
			};
		case BadgeActionTypes.SET_BADGE_STUDIO_BARCODE: {
			const barcodes = [...state.spaceObjects.BarCodes];
			const { itemId, newItem } = action.payload;
			const idx = barcodes.findIndex(bc => bc.Id === itemId);
			if (idx > -1) {
				barcodes.splice(idx, 1, newItem);
			}

			return {
				...state,
				spaceObjects: {
					...state.spaceObjects,
					BarCodes: barcodes,
				},
			};
		}
		case BadgeActionTypes.SET_BADGE_STUDIO_IMAGE: {
			const images = [...state.spaceObjects.Images];
			const { newItem, itemId } = action.payload;
			const idx = images.findIndex(bc => bc.Id === itemId);
			if (idx > -1) {
				images.splice(idx, 1, newItem);
			}

			return {
				...state,
				spaceObjects: {
					...state.spaceObjects,
					Images: images,
				},
			};
		}
		case BadgeActionTypes.SET_BADGE_STUDIO_MAGSTRIPE: {
			const magstripes = [...state.spaceObjects.BadgeMagstripes];
			const { newItem, itemId } = action.payload;
			const idx = magstripes.findIndex(bc => bc.Id === itemId);
			if (idx > -1) {
				magstripes.splice(idx, 1, newItem);
			}

			return {
				...state,
				spaceObjects: {
					...state.spaceObjects,
					BadgeMagstripes: magstripes,
				},
			};
		}
		case BadgeActionTypes.SET_GRAPHIC_TEXT: {
			const texts = [...state.spaceObjects.Texts];
			const { newItem, itemId } = action.payload;
			const idx = texts.findIndex(bc => bc.Id === itemId);
			if (idx > -1) {
				texts.splice(idx, 1, newItem);
			}

			return {
				...state,
				spaceObjects: {
					...state.spaceObjects,
					Texts: texts,
				},
			};
		}
		case BadgeActionTypes.SET_VISUAL_SHAPE_OBJECT: {
			const images = [...state.spaceObjects.ShapeObjects];
			const { newItem, itemId } = action.payload;
			const idx = images.findIndex(bc => bc.Id === itemId);
			if (idx > -1) {
				images.splice(idx, 1, newItem);
			}

			return {
				...state,
				spaceObjects: {
					...state.spaceObjects,
					ShapeObjects: images,
				},
			};
		}
		case BadgeActionTypes.SET_BADGE_STUDIO_INFO: {
			return {
				...state,
				badgeInfo: {
					...state.badgeInfo,
					...action.payload,
				},
			};
		}
		case BadgeActionTypes.SET_FONTS: {
			return {
				...state,
				fontFamilies: action.payload,
			};
		}
		case BadgeActionTypes.SET_UDFS: {
			return {
				...state,
				udfs: action.payload,
			};
		}
		case BadgeActionTypes.SET_UDFS_256: {
			return {
				...state,
				udfs256: action.payload,
			};
		}
		case BadgeActionTypes.SET_CURRENT_REDLINE_CONFIG_VALUES:
			return {
				...state,
				currentRedlineConfigValues: action.payload,
			};
		case BadgeActionTypes.SET_DISABLE_MENU_OPTS:
			return {
				...state,
				drawingMenuOptsDisabled: action.payload,
			};
		case BadgeActionTypes.SET_GRAPHIC_DATA:
			return {
				...state,
				graphicInfo: {
					...state.graphicInfo,
					...action.payload,
				},
			};
		case BadgeActionTypes.SET_DROP_DATA_NODES:
			return {
				...state,
				dropDataNodes: action.payload,
			};
		case BadgeActionTypes.SET_HAS_CHANGED:
			return {
				...state,
				hasChanged: action.payload,
			};
		case BadgeActionTypes.SET_VIEW_MODE:
			return {
				...state,
				viewMode: action.payload,
			};
		default:
			return state;
	}
};

export { badgeReducer };
